import { Button, Input, Select, SelectValue, Textarea, toast } from '@platform/ui-kit'
import { Controller, useForm } from 'react-hook-form'
import { EMAIL_VALIDATION, REQUIRED_FIELD } from '@platform/utils'
import classnames from 'classnames'
import { useMutation } from '@tanstack/react-query'

import styles from './index.module.scss'

type FormValues = {
  name: string
  email: string
  topic: SelectValue
  content: string
}

type ContactUsFormProps = {
  className?: string
}

const TOPIC_OPTIONS = [
  {
    value: 'HIRE_TALENT',
    label: 'Hire talent',
  },
  {
    value: 'APPLY_FOR_JOBS',
    label: 'Apply for jobs',
  },
  {
    value: 'MEDIA_PRESS',
    label: 'Media & press',
  },
  {
    value: 'OTHER',
    label: 'Other',
  },
]

type ContactUsType = {
  name: string
  email: string
  topic: string
  content: string
}
const fetcher = async (body: ContactUsType) => {
  const apiUrl =
    process.env['NEXT_PUBLIC_WEBSITE_API_URL'] || 'https://platform-api.olby.com/website'

  const url = `${apiUrl}/contact-us/`

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  })

  return res
}
export const ContactUsForm = ({ className }: ContactUsFormProps) => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onSubmit',
  })

  const { isLoading, mutate } = useMutation(fetcher, {
    onSuccess: () => {
      toast({ title: 'Your message was successfully sent!' })
      reset()
    },
  })

  const onSubmit = handleSubmit(formData => {
    const payload = {
      ...formData,
      topic: formData.topic.label as string,
    }
    mutate(payload)
  })

  return (
    <form className={classnames(styles['wrapper'], className)} onSubmit={onSubmit} noValidate>
      <Input
        label="Your name"
        id="name"
        placeholder="Full name"
        error={errors.name?.message}
        {...register('name', { required: REQUIRED_FIELD })}
      />
      <Input
        label="Your email"
        placeholder="Email"
        id="email"
        type="email"
        error={errors.email?.message}
        {...register('email', { required: REQUIRED_FIELD, validate: EMAIL_VALIDATION })}
      />
      <Controller
        name="topic"
        control={control}
        rules={{ required: REQUIRED_FIELD }}
        render={({ field: { onChange, value } }) => (
          <Select
            aria-labelledby="topic"
            id="topic"
            label="Topic"
            value={value as SelectValue}
            onChange={onChange}
            options={TOPIC_OPTIONS}
            placeholder="Select..."
            error={errors.topic?.message}
          />
        )}
      />
      <Textarea
        id="content"
        label="How can we help?"
        rows={8}
        error={errors.content?.message}
        {...register('content', { required: REQUIRED_FIELD })}
      />
      <Button
        type="submit"
        variant="primary"
        size="large"
        className={styles['button']}
        isLoading={isLoading}
      >
        Send message
      </Button>
    </form>
  )
}
